import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { FaImages } from 'react-icons/fa'
import ScrollToTopButton from '../components/ScrollToTopButton'
import MobileQuoteButton from '../components/MobileQuoteButton'
import MondayManagement from '../pages/admin/MondayManagement'
import GalleryModal from '../components/GalleryModal'
import { useGalleries, useGallery } from '../lib/galleries'

const FORM_THANK_YOU_MAP = {
  estimados_2026_new_app: '/forms/architecture/new-quote/thank-you',
  estimados_2026_new_scope: '/forms/architecture/new-scope/thank-you',
}

const DETAIL_IMAGES = [
  '/archeng/1.webp',
  '/archeng/2.webp',
  '/archeng/3.webp',
  '/archeng/4.webp',
]

const ArchitectureSection = () => {
  const [showMondayForm, setShowMondayForm] = useState(false)
  const [activeFormType, setActiveFormType] = useState(null)
  const preModalPath = useRef(null)
  const { t } = useTranslation()
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [activeGalleryKey, setActiveGalleryKey] = useState(null)
  const { galleries } = useGalleries()
  const { images: activeGalleryImages, loading: galleryLoading } = useGallery(activeGalleryKey)
  const archGallery = galleries.find(g => g.service_key === 'architecture')

  const FORM_PATHS = {
    estimados_2026_new_app: '/forms/architecture/new-quote',
    estimados_2026_new_scope: '/forms/architecture/new-scope',
  }

  const openModal = (formType) => {
    preModalPath.current = window.location.pathname
    window.history.pushState({ modal: true }, '', FORM_PATHS[formType] || '/forms/architecture/new-quote')
    setActiveFormType(formType)
    setShowMondayForm(true)
  }

  const closeModal = () => {
    setShowMondayForm(false)
    window.history.replaceState(null, '', preModalPath.current || '/architecture')
  }

  useEffect(() => {
    const handlePopState = () => setShowMondayForm(false)
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Retrieve services list from locale
  const servicesList = t('architecture.services_list', { returnObjects: true })
  const safeServicesList = Array.isArray(servicesList) ? servicesList : []

  // Merge with static IDs and images
  const services = safeServicesList.map((service, index) => ({
    ...service,
    image: DETAIL_IMAGES[index]
  }))

  return (
    <section
      className="text-white bg-black"
      aria-labelledby="architecture-heading"
      data-section="architecture"
      id="architecture"
    >
      {/* Hero Section */}
      <div className="relative min-h-screen sm:h-screen sm:max-h-[1080px] w-full overflow-hidden">
        <div className="absolute inset-0">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="w-full h-full"
          >
            <img
              src="/archeng/hero.webp"
              alt={t('architecture.hero_alt', 'Architecture and Engineering Blueprint')}
              className="object-cover w-full h-full"
            />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-r to-transparent from-black/90 via-black/50" />
        </div>

        <div className="relative z-10 h-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-20 py-24 flex flex-col justify-end sm:justify-between">
          <div className="max-w-4xl">
            <motion.h1
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
              id="architecture-heading"
              className="text-4xl sm:text-7xl md:text-8xl lg:text-[7rem] font-black leading-[0.9] tracking-tighter uppercase mb-8 sm:mb-12 lg:mb-16"
            >
              <Trans i18nKey="architecture.hero_title" />
            </motion.h1>
          </div>

          <div className="flex flex-wrap gap-6 justify-between items-end w-full">
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.4 }}
              className="max-w-2xl text-xl font-medium leading-relaxed sm:text-2xl"
            >
              <Trans
                i18nKey="architecture.hero_text_1"
                components={{ 1: <span className="font-bold" />, 3: <span className="font-bold" /> }}
              />
              <br />
              <br />
              <Trans
                i18nKey="architecture.hero_text_2"
                components={{ 1: <span className="font-bold" /> }}
              />
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.6 }}
              className="hidden sm:flex flex-shrink-0"
            >
              <button
                onClick={() => openModal('estimados_2026_new_app')}
                className="px-8 py-4 text-sm font-bold tracking-wider text-black uppercase transition-colors bg-primary hover:bg-yellow-400"
              >
                {t('admin.monday.new_application')}
              </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Services Header */}
      <div className="px-4 pt-24 pb-12 bg-black sm:px-12 lg:px-20">
        <div className="max-w-[1920px] mx-auto">
          <div className="inline-block relative">
            <h2 className="relative z-10 pb-2 text-5xl font-bold text-white sm:text-6xl">
              <motion.span
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="block"
              >
                {t('architecture.services_title_our')}
              </motion.span>
              <div className="inline-block relative">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: '100%' }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2, ease: 'circOut' }}
                  className="absolute inset-0 z-0 bg-primary"
                />
                <span className="relative z-10 px-2 text-black text-secondary">{t('architecture.services_title_services')}</span>
              </div>
            </h2>
          </div>
        </div>
      </div>

      {/* Services List */}
      {services.map((service, index) => (
        <div key={service.id} className="px-4 py-24 bg-black sm:px-12 lg:px-20">
          <div className="max-w-[1920px] mx-auto grid gap-16 lg:grid-cols-2 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className={`relative h-[400px] sm:h-[500px] lg:h-[600px] ${index % 2 === 0 ? '' : 'lg:order-2'}`}
            >
              <motion.img
                src={service.image}
                alt={service.title}
                className="object-cover w-full h-full"
                initial={{ filter: 'grayscale(100%)' }}
                whileInView={{ filter: 'grayscale(0%)' }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className={index % 2 === 0 ? '' : 'lg:order-1'}
            >
              <div className="flex flex-col justify-center h-full">
                <span className="mb-6 text-5xl font-bold text-gray-800 sm:text-6xl">{service.id}</span>
                <h3 className="mb-8 text-3xl font-bold leading-tight text-white sm:text-4xl md:text-5xl">
                  {service.title}
                </h3>
                <p className="mb-8 max-w-xl text-lg leading-relaxed text-gray-300 sm:text-xl">
                  {service.description}
                </p>

                <div className="mb-10">
                  <p className="mb-4 text-lg font-semibold text-white">{t('architecture.services_include')}</p>
                  <ul className="space-y-3">
                    {service.bullets.map((bullet) => (
                      <li key={bullet} className="text-lg text-gray-400">
                        – {bullet}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col gap-4 w-full max-w-xl">
                  <div className="flex flex-wrap gap-4">
                    <button
                      onClick={() => {
                        openModal('estimados_2026_new_app')
                      }}
                      className="flex-1 min-w-[220px] px-8 py-4 bg-primary text-black font-bold text-sm tracking-wider uppercase hover:bg-yellow-400 transition-colors text-center"
                    >
                      {t('admin.monday.new_application')}
                    </button>
                    {archGallery && (
                      <button
                        onClick={() => { setActiveGalleryKey('architecture'); setGalleryOpen(true) }}
                        className="flex-1 min-w-[220px] inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-bold tracking-wider uppercase border-2 border-white text-white hover:bg-white hover:text-black transition-all duration-200"
                      >
                        <FaImages />
                        {t('construction.view_gallery')}
                      </button>
                    )}
                    {/* New Scope button temporarily hidden */}
                    {/* <button
                      onClick={() => {
                        setActiveFormType('estimados_2026_new_scope')
                        setShowMondayForm(true)
                      }}
                      className="flex-1 min-w-[220px] px-8 py-4 bg-white text-black font-bold text-sm tracking-wider uppercase hover:bg-gray-100 transition-colors border border-gray-300 text-center"
                    >
                      {t('admin.monday.new_scope')}
                    </button> */}
                  </div>

                  <Link
                    to="/about"
                    className="inline-block px-8 py-4 text-sm font-bold tracking-wider text-center text-white uppercase transition-colors bg-white/10 hover:bg-white/20"
                  >
                    {t('architecture.learn_more')}
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      ))}

      {/* Monday Form Modal */}
      {showMondayForm && (
        <div className="overflow-y-auto fixed inset-0 z-50">
          <div className="flex justify-center items-center p-4 min-h-screen">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={closeModal}
            />

            {/* Modal Content */}
            <div className="relative bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              <div className="overflow-y-auto max-h-[90vh]">
                <MondayManagement
                  session={null}
                  onReturn={closeModal}
                  isPublicMode={true}
                  defaultFormType={activeFormType}
                  thankYouPath={FORM_THANK_YOU_MAP[activeFormType]}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <GalleryModal images={activeGalleryImages.map(img => img.url)} isOpen={galleryOpen} onClose={() => setGalleryOpen(false)} loading={galleryLoading} />
      <MobileQuoteButton onClick={() => openModal('estimados_2026_new_app')} />
      <ScrollToTopButton />
    </section>
  )
}
export default ArchitectureSection
